import './styles/graphic.scss';
import * as d3 from 'd3';
import nationalMapSvg from './img/svg/national-map.svg';
import nationalMapMobileSvg from './img/svg/national-map-mobile.svg';
import metaData from './data/meta.json';

export default function (props, container, isMobile, meta = {}) {
  const {
    initialWidth,
    initialHeight,
    // width,
    // height
  } = props;

  const containerDiv = container.append('div')
    .attr('class', 'container');

  const legend = containerDiv.append('div')
    .attr('class', 'legend');

  legend.append('div')
    .attr('class', 'legend-title')
    .text('Status of redistricting efforts');

  const legendCards = legend.append('div')
    .attr('class', 'legend-cards');

  // Add map container
  const mapContainer = containerDiv.append('div')
    .attr('class', 'map-container');

  // Add data note container
  const dataNote = containerDiv.append('div')
    .attr('class', 'data-note');

  // Update headline and dek from meta.json
  d3.select('.headline').text(metaData.headline);
  d3.select('.dek').text(metaData.subhed);

  // Add footnote if it exists (below the map)
  if (metaData.footnote) {
    dataNote.append('p')
      .text(metaData.footnote);
  }

  // Detect parent URL (with retry logic for Pym initialization)
  const detectParentUrl = () => {
    const checkParentUrl = () => {
      if (window.pymChild && window.pymChild.parentUrl) {
        console.log('Parent URL (from Pym):', window.pymChild.parentUrl);
        return window.pymChild.parentUrl;
      } if (document.referrer) {
        console.log('Parent URL (from referrer):', document.referrer);
        return document.referrer;
      }
      console.log('Parent URL: Not available (standalone mode)');
      return null;
    };

    // Try immediately
    let parentUrl = checkParentUrl();

    // If not available, try again after Pym initializes
    if (!parentUrl) {
      setTimeout(() => {
        parentUrl = checkParentUrl();
      }, 100);
      setTimeout(() => {
        parentUrl = checkParentUrl();
      }, 500);
    }

    return parentUrl;
  };

  const parentUrl = detectParentUrl();

  // Check if parent URL does NOT contain specific strings and adjust background
  setTimeout(() => {
    const currentParentUrl = (window.pymChild && window.pymChild.parentUrl) || document.referrer;
    const keepGreyBackground = currentParentUrl && (
      currentParentUrl.includes('state-redistricting-maps-vis')
      // || currentParentUrl.includes('localhost')
      || currentParentUrl.includes('ix-cnn-io-stage')
    );

    if (currentParentUrl && !keepGreyBackground) {
      // Inject a style tag to override CSS with high specificity
      const style = document.createElement('style');
      style.innerHTML = `
        root, html, body, #root, .container, .wrapper, .legend {
          background-color: white !important;
        }
      `;
      document.head.appendChild(style);
      console.log('Background adjusted to white for:', currentParentUrl);
    } else {
      console.log('Keeping grey background (#f6f6f6) for:', currentParentUrl);
    }
  }, 600);

  // Fetch data from states endpoint
  const dataUrl = 'https://ix.cnn.io/data/redistricting/redistricting-states-data.json';

  fetch(dataUrl)
    .then(response => response.json())
    .then((statesData) => {
      // Use seat counts from meta.json instead of aggregating from API
      const aggregateData = {
        enacted: {
          rep: metaData.enacted_rep,
          dem: metaData.enacted_dem,
        },
        pending: {
          rep: metaData.pending_rep,
          dem: metaData.pending_dem,
        },
        'under discussion': { rep: 0, dem: 0 },
      };

      console.log('Seat data from meta.json:', aggregateData);

      // Legend card configuration based on fetched data
      const cardsData = [
        {
          title: 'Map enacted',
          squares: ['enacted-dem', 'enacted-rep'],
          seats: [
            aggregateData.enacted.rep > 0 && {
              number: `+${aggregateData.enacted.rep}`,
              text: aggregateData.enacted.rep === 1 ? 'Rep. seat' : 'Rep. seats',
              type: 'rep',
              count: aggregateData.enacted.rep,
            },
            aggregateData.enacted.dem > 0 && {
              number: `+${aggregateData.enacted.dem}`,
              text: aggregateData.enacted.dem === 1 ? 'Dem. seat' : 'Dem. seats',
              type: 'dem',
              count: aggregateData.enacted.dem,
            },
          ].filter(Boolean),
        },
        {
          title: 'Map pending',
          squares: ['pending-dem', 'pending-rep'],
          seats: [
            aggregateData.pending.dem > 0 && {
              number: `+${aggregateData.pending.dem}`,
              text: aggregateData.pending.dem === 1 ? 'Dem. seat' : 'Dem. seats',
              type: 'dem',
              count: aggregateData.pending.dem,
            },
            aggregateData.pending.rep > 0 && {
              number: `+${aggregateData.pending.rep}`,
              text: aggregateData.pending.rep === 1 ? 'Rep. seat' : 'Rep. seats',
              type: 'rep',
              count: aggregateData.pending.rep,
            },
          ].filter(Boolean),
        },
        {
          title: 'Under discussion',
          squares: ['discussion-dem', 'discussion-rep'],
          description: 'There are no states still considering new maps for the 2026 election.',
        },
      ];

      // Create cards
      cardsData.forEach((cardData) => {
        const card = legendCards.append('div')
          .attr('class', `legend-card ${cardData.title.toLowerCase().replace(' ', '-')}`);

        const header = card.append('div')
          .attr('class', 'legend-card-header');

        header.append('span')
          .attr('class', 'legend-card-title')
          .text(cardData.title);

        // Add squares
        if (cardData.squares.length > 1) {
          const squaresContainer = header.append('div')
            .attr('class', 'legend-square double');

          cardData.squares.forEach((square) => {
            squaresContainer.append('div')
              .attr('class', `legend-square-${square}`);
          });
        } else {
          header.append('div')
            .attr('class', `legend-square-${cardData.squares[0]}`);
        }

        const content = card.append('div')
          .attr('class', 'legend-card-content');

        // Add seats or description
        if (cardData.seats) {
          cardData.seats.forEach((seat) => {
            const seatCount = content.append('div')
              .attr('class', 'seat-count');

            const seatLabel = seatCount.append('span')
              .attr('class', `seat-label ${seat.type}`);

            seatLabel.append('span')
              .attr('class', 'seat-number')
              .text(seat.number);

            seatLabel.append('span')
              .attr('class', 'seat-text')
              .text(` ${seat.text}`);

            const seatSquares = seatCount.append('div')
              .attr('class', 'seat-squares');

            for (let i = 0; i < seat.count; i += 1) {
              seatSquares.append('div')
                .attr('class', `seat-square ${seat.type}`);
            }
          });

          // If no seats were added (empty array), show "No seats pending" for Map pending card
          if (cardData.seats.length === 0 && cardData.title === 'Map pending') {
            content.append('p')
              .attr('class', 'card-description')
              .text('No seats pending in any states.');
          }
        } else if (cardData.description) {
          content.append('p')
            .attr('class', 'card-description')
            .text(cardData.description);
        }
      });

      // Load and display the appropriate SVG based on device
      const svgToLoad = isMobile ? nationalMapMobileSvg : nationalMapSvg;

      d3.xml(svgToLoad).then((data) => {
        mapContainer.node().appendChild(data.documentElement);

        // Color the states on the map based on the data
        const svg = mapContainer.select('svg');

        statesData.forEach((state) => {
          const stateElement = svg.select(`#${state.state_abbr}`);
          if (!stateElement.empty()) {
            const party = state.party_driving_efforts.toLowerCase();
            const status = state.status.toLowerCase().replace(' ', '-');

            // Add classes for styling
            stateElement.attr('class', `state ${status} ${party}`);

            // Store data on the element for potential interactions
            stateElement.datum(state);
          }
        });

        if (window.pymChild) {
          window.pymChild.sendHeight();
        }
      }).catch((error) => {
        console.error('Error loading SVG:', error);
      });
    })
    .catch((error) => {
      console.error('Error fetching states data:', error);

      // Fallback: still load the map even if data fetch fails
      const svgToLoad = isMobile ? nationalMapMobileSvg : nationalMapSvg;

      d3.xml(svgToLoad).then((data) => {
        mapContainer.node().appendChild(data.documentElement);

        if (window.pymChild) {
          window.pymChild.sendHeight();
        }
      }).catch((svgError) => {
        console.error('Error loading SVG:', svgError);
      });
    });
}
